/**
 * Character roster. Optional UI fields per entry:
 * `thumbSrc` — grid portrait; `portraitSrc` — detail modal art; `skillIcons` — [p1, p2, active] image URLs.
 */
export const CHARACTERS = [
  {
    id: 'kira',
    code: 'KR',
    name: 'Kira "Wraith" Renko',
    role: 'Skillful speedrunner',
    quote: 'She was declared dead three times. She approved each report.',
    tags: ['Suppressed Pistol', '+Move Speed', 'Low HP Pool'],
    starterLoadout: ['shinkansen_pistol', 'shinkansen_pistol'],
    starterRarity: 'uncommon',
    stats: { maxHp: 6, speedMul: 1.16, fireRateMul: 1.03, damageMul: 1.0 },
    passives: [
      { name: 'Ghost Protocol', text: 'Kill quickly after room entry to delay nearby enemy alert.' },
      { name: 'Silhouette', text: 'Dash through enemies to stagger instead of dealing direct damage.' },
    ],
    active: { name: 'Vanish', text: 'Become invisible briefly. First break-shot gains bonus damage.', cooldown: 12 },
    unlockKills: 0,
    thumbSrc: '/assets/2D/IconPlayer/Kira-NHL.png',
    portraitSrc: '/assets/2D/IconPlayer/Kira-NHL.png',
  },
  {
    id: 'dax',
    code: 'DM',
    name: 'Dax "Ironwall" Muren',
    role: 'Beginner-friendly tank',
    quote: 'He has been shot fourteen times. He stopped counting after the first six.',
    tags: ['Shotgun', '+HP Pool', 'Slow Move Speed'],
    starterLoadout: ['reiketsu_shotgun', 'reiketsu_shotgun'],
    starterRarity: 'common',
    stats: { maxHp: 10, speedMul: 0.92, fireRateMul: 0.95, damageMul: 1.0 },
    passives: [
      { name: 'Armor Plating', text: 'Negate the first incoming hit per room entry.' },
      { name: 'Suppress Fire', text: 'Fast repeat pellet hits apply short slow.' },
    ],
    active: { name: 'Bulwark', text: 'Plant in place to reduce incoming damage and boost fire cadence.', cooldown: 15 },
    unlockKills: 120,
    thumbSrc: '/assets/2D/IconPlayer/Dax-NHL.png',
    portraitSrc: '/assets/2D/IconPlayer/Dax-NHL.png',
  },
  {
    id: 'yael',
    code: 'YS',
    name: 'Yael "Static" Sorn',
    role: 'Fragment-chaser scaler',
    quote: 'She does not fight enemies. She corrupts them.',
    tags: ['SMG', '+Fragment Synergy', 'Weak Solo Damage'],
    starterLoadout: ['jinsoku_smg', 'jinsoku_smg'],
    starterRarity: 'uncommon',
    stats: { maxHp: 7, speedMul: 1.02, fireRateMul: 1.04, damageMul: 0.9 },
    passives: [
      { name: 'Signal Jam', text: 'Nearby enemies can misfire.' },
      { name: 'Overclocked', text: 'Each collected fragment increases fire-rate (stacking).' },
    ],
    active: { name: 'Pulse Burst', text: 'Short EMP pulse that stuns enemies and drops hostile projectiles.', cooldown: 18 },
    unlockKills: 260,
    thumbSrc: '/assets/2D/IconPlayer/Yael-NHL.png',
    portraitSrc: '/assets/2D/IconPlayer/Yael-NHL.png',
  },
  {
    id: 'rook',
    code: 'RV',
    name: 'Rook "Vanta" Vasilis',
    role: 'High-tension sniper',
    quote: 'He learned patience in a room with no windows. He brought it back out.',
    tags: ['Sniper Rifle', '+Damage Per Shot', 'Very Low Fire Rate'],
    starterLoadout: ['stormcaller_smr2', 'shinkansen_pistol'],
    starterRarity: 'rare',
    stats: { maxHp: 7, speedMul: 0.95, fireRateMul: 0.78, damageMul: 1.22 },
    passives: [
      { name: 'Patience Tax', text: 'Deliberate pacing between shots grants bonus damage.' },
      { name: 'Piercing Round', text: 'Shots penetrate the first target.' },
    ],
    active: { name: 'Mark & Execute', text: 'Tag target; next shot deals amplified damage and stuns nearby foes.', cooldown: 20 },
    unlockKills: 520,
    thumbSrc: '/assets/2D/IconPlayer/Rook-NHL.png',
    portraitSrc: '/assets/2D/IconPlayer/Rook-NHL.png',
    thumbPosition: '62% 14%',
    portraitPosition: '62% 50%',
  },
  {
    id: 'esme',
    code: 'EM',
    name: 'Esme "Fracture" Mallow',
    role: 'Spatial chaos wildcard',
    quote: 'She plans an exit before she plans the entry. Usually they are the same hole.',
    tags: ['Grenade Launcher', '+AoE Potential', 'Self-Danger Close Range'],
    starterLoadout: ['jigoku_launcher', 'shinkansen_pistol'],
    starterRarity: 'rare',
    stats: { maxHp: 8, speedMul: 0.98, fireRateMul: 0.9, damageMul: 1.1 },
    passives: [
      { name: 'Controlled Demo', text: 'Wall-bounced grenades deal extra damage.' },
      { name: 'Blast Radius', text: 'Kill zones can leave temporary burning ground.' },
    ],
    active: { name: 'Breach Charge', text: 'Blast open a wall segment and permanently alter room routing.', cooldown: 22 },
    unlockKills: 860,
  },
];

const PROGRESSION_KEY = 'oneman.characterProgress.v1';
const CHARACTER_PICK_KEY = 'oneman.selectedCharacter.v1';

export function getCharacterById(id) {
  return CHARACTERS.find((ch) => ch.id === id) || CHARACTERS[0];
}

export function getSelectedCharacterId() {
  try {
    const id = localStorage.getItem(CHARACTER_PICK_KEY);
    return getCharacterById(id).id;
  } catch {
    return CHARACTERS[0].id;
  }
}

export function setSelectedCharacterId(id) {
  try {
    localStorage.setItem(CHARACTER_PICK_KEY, getCharacterById(id).id);
  } catch {
    // Ignore storage failures.
  }
}

export function getCharacterProgress() {
  try {
    const parsed = JSON.parse(localStorage.getItem(PROGRESSION_KEY) || '{}');
    const totalKills = Number.isFinite(parsed.totalKills) ? Math.max(0, parsed.totalKills) : 0;
    return { totalKills };
  } catch {
    return { totalKills: 0 };
  }
}

export function addCharacterProgressKills(count = 1) {
  const current = getCharacterProgress();
  const next = { totalKills: current.totalKills + Math.max(0, count) };
  try {
    localStorage.setItem(PROGRESSION_KEY, JSON.stringify(next));
  } catch {
    // Ignore storage failures.
  }
  return next;
}

export function isCharacterUnlocked(id, progress = getCharacterProgress()) {
  const ch = getCharacterById(id);
  return progress.totalKills >= (ch.unlockKills || 0);
}

export function getUnlockDelta(id, progress = getCharacterProgress()) {
  const ch = getCharacterById(id);
  return Math.max(0, (ch.unlockKills || 0) - progress.totalKills);
}

/** Passive / passive / active slots for character detail UI. */
export function getCharacterSkillSlots(ch) {
  const a = ch.active;
  return [
    { section: 'PASSIVE', name: ch.passives[0].name, text: ch.passives[0].text },
    { section: 'PASSIVE', name: ch.passives[1].name, text: ch.passives[1].text },
    {
      section: 'ACTIVE',
      name: a.name,
      text: `${a.text} Cooldown: ${a.cooldown}s.`,
    },
  ];
}

